import { Package, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useStockStatus } from "@/hooks/notifications/useStockStatus";
import { cn } from "@/lib/utils";

interface StockStatusBadgeProps {
  notificationId: string;
  onClick?: () => void;
}

export const StockStatusBadge = ({ notificationId, onClick }: StockStatusBadgeProps) => {
  const { data: stockStatus, isLoading, error, isFetching } = useStockStatus(notificationId);

  // Show subtle loader only on first load
  if (isLoading && !stockStatus) {
    return (
      <Badge variant="secondary" className="gap-1 opacity-60">
        <Loader2 className="h-3 w-3 animate-spin" />
        Laden...
      </Badge>
    );
  }

  // Keep last known state on error, show error only if no cached data
  if (error && !stockStatus) {
    console.error('Error loading stock status for', notificationId, error);
    return null;
  }

  if (!stockStatus) {
    return null;
  }

  if (stockStatus.totalProducts === 0) {
    return (
      <Badge variant="outline" className="gap-1 opacity-50 text-xs">
        <Package className="h-3 w-3" />
        Geen monitoring
      </Badge>
    );
  }

  const { totalProducts, productsInStock } = stockStatus;

  let variant: "destructive" | "warning" | "secondary" = "secondary";
  
  if (productsInStock === totalProducts && totalProducts > 0) {
    variant = "destructive";
  } else if (productsInStock > 0 && productsInStock < totalProducts) {
    variant = "warning";
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  return (
    <Badge 
      variant={variant} 
      className={cn(
        "gap-1 transition-all duration-300",
        onClick && "cursor-pointer hover:scale-110",
        variant === "destructive" && "animate-pulse shadow-lg shadow-destructive/50 font-semibold",
        isFetching && "opacity-70"
      )}
      onClick={onClick ? handleClick : undefined}
    >
      <Package className="h-3 w-3" />
      {productsInStock}/{totalProducts} op voorraad
    </Badge>
  );
};
